import type { SignUpFormValues } from '../../schemas';

import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useSnapshot } from 'valtio/react';

import { Button } from '@paid-ui/components/button';
import { FormControl, FormField, FormItem } from '@paid-ui/components/form';
import { OneTimeCodeInput } from '@paid-ui/components/one-time-code-input';
import { UnstyledButton } from '@paid-ui/components/unstyled-button';
import { useSendOneTimeCodeToEmail } from '@paid-ui/mutations/send-one-time-code';
import { useToast } from '@paid-ui/ui/toast';

import { signUpManager } from '../../models';
import { SignUpStep } from '../../models/enums';
import { useSignUp } from '../../mutations/sign-up';

interface Props {
  disabled?: boolean;
  goToStep?: (step: SignUpStep) => void;
}

export const StepOneTimeCode: React.FC<Props> = ({ disabled, goToStep }) => {
  const toast = useToast();
  const form = useFormContext();
  const { currentStep } = useSnapshot(signUpManager);
  const { mutate, isLoading: isSending } = useSendOneTimeCodeToEmail();
  const { mutate: signUp, isLoading: isSubmitting } = useSignUp();
  const oneTimeCode = form.watch('oneTimeCode');
  const isValid = Boolean(oneTimeCode) && !form.getFieldState('oneTimeCode').invalid;

  const sendOneTimeCode = () => {
    const account = form.getValues('email');

    if (!account) {
      toast.error('Please enter your email address.');
      return;
    }

    mutate({ account });
    form.setValue('oneTimeCode', '');
  };

  const handleSubmit = () => {
    signUp(form.getValues() as SignUpFormValues);
  };

  const handleEmailAddressChange = () => {
    goToStep?.(SignUpStep.CHANGE_EMAIL);
  };

  useEffect(() => {
    if (currentStep !== SignUpStep.ONE_TIME_CODE) return;
    sendOneTimeCode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep]);

  if (currentStep !== SignUpStep.ONE_TIME_CODE) {
    return null;
  }

  return (
    <div className="space-y-9">
      <div className="flex flex-col items-center gap-y-3.5">
        <FormField
          name="oneTimeCode"
          control={form.control}
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <OneTimeCodeInput
                  {...field}
                  autoFocus
                  disabled={disabled || isSubmitting}
                  onEnter={handleSubmit}
                />
              </FormControl>
            </FormItem>
          )}
        />
        <p className="text-center text-base">
          <span>Didn't receive a code? </span>
          <UnstyledButton
            disabled={disabled || isSending || isSubmitting}
            className="cursor-pointer text-blue hover:text-blue-dark disabled:text-grey-tertiary"
            onClick={sendOneTimeCode}
          >
            Click to resend
          </UnstyledButton>
          <span> or </span>
          <UnstyledButton
            disabled={disabled || isSending || isSubmitting}
            className="cursor-pointer text-blue hover:text-blue-dark disabled:text-grey-tertiary"
            onClick={handleEmailAddressChange}
          >
            Edit email address
          </UnstyledButton>
        </p>
      </div>
      <Button
        variant="solid"
        className="h-[44px]"
        disabled={disabled || isSending || !isValid}
        loading={isSubmitting}
        onClick={handleSubmit}
      >
        {isSubmitting ? 'Submitting...' : 'Continue'}
      </Button>
    </div>
  );
};
